/**
 * Coordinate conversion utilities for the measure overlay tools.
 *
 * Conversion pipeline:
 *   screen px → PDF points → PDF inches → image search px / real inches → formatted string
 *
 * Key constants:
 *   - HI_DPI_FACTOR = 2 (PdfRenderService renders at 2× for retina displays)
 *   - PDF points per inch = 72
 *   - Default DPI for image search = 600 (from SearchConfiguration.default_dpi)
 *
 * Related: Issue #1212 — Ruler & Area Selector Tools
 *
 * Issue #2442 added a second entry point to each formatter that takes PDF points directly
 * (`formatDistancePoints`, `formatRectanglePoints`). The overlay stores a measurement as fractions
 * of the page and multiplies by the page's size in points, so the physical result does not depend
 * on zoom, device pixel ratio or the size of the rendered canvas. The screen-pixel entry points
 * remain for callers that only know the canvas.
 */

/** Default DPI assumed for image-search-pixel conversion. */
export const DEFAULT_IMAGE_SEARCH_DPI = 600;

/** PDF specification: 72 points per inch. */
export const PDF_POINTS_PER_INCH = 72;

/** PdfRenderService HI-DPI factor. */
export const HI_DPI_FACTOR = 2;

/** Inches per meter. */
export const INCHES_PER_METER = 39.3701;

// ============================================================================
// Core Conversion Functions
// ============================================================================

/**
 * Convert a screen-pixel distance (on the overlay) to PDF points.
 *
 * The canvas is rendered at `currentScale` (which includes HI_DPI_FACTOR),
 * but displayed at `currentScale / HI_DPI_FACTOR` CSS pixels per PDF point.
 * So: displayScale = currentScale / HI_DPI_FACTOR
 *     pdfPoints = screenPx / displayScale
 */
export function screenToPdfPoints(screenPx: number, currentScale: number): number {
  const displayScale = currentScale / HI_DPI_FACTOR;
  return displayScale > 0 ? screenPx / displayScale : 0;
}

/**
 * Convert PDF points to physical inches.
 *
 * @param correctionFactor For scanned PDFs with bogus MediaBox (pixels-as-points),
 *   this factor corrects the nominal PDF-point dimensions to real inches.
 *   Default 1.0 means the MediaBox is trustworthy.
 */
export function pdfPointsToInches(pdfPoints: number, correctionFactor: number = 1.0): number {
  return (pdfPoints / PDF_POINTS_PER_INCH) * correctionFactor;
}

/** Convert PDF points to image-search pixels at the given DPI. */
export function pdfPointsToImagePx(pdfPoints: number, dpi: number = DEFAULT_IMAGE_SEARCH_DPI): number {
  return pdfPoints * (dpi / PDF_POINTS_PER_INCH);
}

/**
 * Apply a drawing scale factor to convert PDF inches to real-world inches.
 * Example: scale = 1/4" = 1'-0" → scaleFactor = 48 (one PDF inch = 48 real inches).
 */
export function applyScale(pdfInches: number, scaleFactor: number): number {
  return pdfInches * scaleFactor;
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format real-world inches the way dimensions are written on drawings.
 * Examples: 132 → `11'-0"`, 57.75 → `4'-9 ¾"`, 6 → `6"`.
 *
 * A length of a foot or more always shows its inches, so eleven feet reads `11'-0"` and cannot be
 * mistaken for a truncated value (Issue #2442).
 *
 * The value is rounded to the nearest 1/16", which is the display tolerance of every physical
 * measurement: a reading is within ±1/32" of the computed length. Integer sixteenths are used
 * internally to avoid floating-point carry-over bugs (e.g., 11.97" rounding to 12" = 1').
 */
export function formatImperial(realInches: number): string {
  const totalSixteenths = Math.round(Math.abs(realInches) * 16);
  if (totalSixteenths === 0) return '0"';

  const feet = Math.floor(totalSixteenths / 192); // 192 = 12 inches × 16
  const remainingSixteenths = totalSixteenths % 192;
  const wholeInches = Math.floor(remainingSixteenths / 16);
  const sixteenths = remainingSixteenths % 16;

  const fractionStr = sixteenths > 0 ? formatFraction(sixteenths / 16) : '';
  const showWholeInches = wholeInches > 0 || feet > 0 || !fractionStr;
  const inchStr = `${showWholeInches ? wholeInches : ''}${showWholeInches ? fractionStr : fractionStr.trim()}"`;

  return feet > 0 ? `${feet}'-${inchStr}` : inchStr;
}

/**
 * Format real-world inches as metric.
 * Shows meters for values >= 1m, otherwise centimeters.
 */
export function formatMetric(realInches: number): string {
  const meters = Math.abs(realInches) / INCHES_PER_METER;
  if (meters >= 1) {
    return `${meters.toFixed(2)} m`;
  }
  const cm = meters * 100;
  return `${cm.toFixed(1)} cm`;
}

/**
 * Format a fractional inch value to nearest 1/16".
 * Returns a Unicode fraction string or empty string for negligible fractions.
 */
function formatFraction(fraction: number): string {
  const sixteenths = Math.round(fraction * 16);
  if (sixteenths === 0 || sixteenths === 16) return '';

  // Reduce to simplest fraction
  const fractionMap: Record<number, string> = {
    1: '¹⁄₁₆',
    2: '⅛',
    3: '³⁄₁₆',
    4: '¼',
    5: '⁵⁄₁₆',
    6: '⅜',
    7: '⁷⁄₁₆',
    8: '½',
    9: '⁹⁄₁₆',
    10: '⅝',
    11: '¹¹⁄₁₆',
    12: '¾',
    13: '¹³⁄₁₆',
    14: '⅞',
    15: '¹⁵⁄₁₆',
  };

  return fractionMap[sixteenths] ? ` ${fractionMap[sixteenths]}` : '';
}

// ============================================================================
// Composite Formatters
// ============================================================================

/** Result of formatting a distance measurement. */
export interface FormattedDistance {
  pdfInches: string;
  imagePx: string;
  physical: string | null; // null while scale is being detected
  physicalMetric: string | null;
  perMille: string; // Per-mille (‰) distance relative to canvas diagonal
}

/**
 * Format a screen-pixel distance into all unit systems.
 * canvasWidth/Height are needed for per-mille calculation.
 * @param correctionFactor Scanned-PDF correction factor (default 1.0).
 */
export function formatDistance(
  screenDistance: number,
  currentScale: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  scaleFactor: number | null = null,
  canvasWidth: number = 0,
  canvasHeight: number = 0,
  correctionFactor: number = 1.0
): FormattedDistance {
  // Per-mille: distance relative to canvas diagonal
  const diagonal = Math.sqrt(canvasWidth * canvasWidth + canvasHeight * canvasHeight);
  const perMille = diagonal > 0 ? (screenDistance / diagonal) * 1000 : 0;

  return formatDistancePoints(
    screenToPdfPoints(screenDistance, currentScale),
    perMille,
    dpi,
    scaleFactor,
    correctionFactor
  );
}

/**
 * Format a distance given in PDF points into all unit systems (Issue #2442).
 *
 * @param pdfPoints Length of the line in PDF points.
 * @param perMille Length of the line relative to the page diagonal, in thousandths.
 * @param scaleFactor Real-world inches per corrected sheet inch, or null when no scale is known.
 * @param correctionFactor Scanned-PDF correction factor (default 1.0).
 */
export function formatDistancePoints(
  pdfPoints: number,
  perMille: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  scaleFactor: number | null = null,
  correctionFactor: number = 1.0
): FormattedDistance {
  const inches = pdfPointsToInches(pdfPoints, correctionFactor);
  const imgPx = pdfPointsToImagePx(pdfPoints, dpi);

  return {
    pdfInches: `${inches.toFixed(1)} in`,
    imagePx: `${Math.round(imgPx)} px`,
    physical: scaleFactor !== null ? formatImperial(applyScale(inches, scaleFactor)) : null,
    physicalMetric: scaleFactor !== null ? formatMetric(applyScale(inches, scaleFactor)) : null,
    perMille: `${Math.round(perMille)} ‰`,
  };
}

/** Formatted coordinate point. */
export interface FormattedPoint {
  pdfInches: string;
  imagePx: string;
  perMille: string; // Normalized 0-1000 relative to canvas width/height
}

/**
 * Format a screen coordinate as a point in multiple unit systems.
 * canvasWidth/Height are needed for per-mille (‰) calculation.
 * @param correctionFactor Scanned-PDF correction factor (default 1.0).
 */
export function formatPoint(
  screenX: number,
  screenY: number,
  currentScale: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  canvasWidth: number = 0,
  canvasHeight: number = 0,
  correctionFactor: number = 1.0
): FormattedPoint {
  return formatPointPoints(
    screenToPdfPoints(screenX, currentScale),
    screenToPdfPoints(screenY, currentScale),
    canvasWidth > 0 ? screenX / canvasWidth : 0,
    canvasHeight > 0 ? screenY / canvasHeight : 0,
    dpi,
    correctionFactor
  );
}

/**
 * Format a position given in PDF points (Issue #2442).
 *
 * @param fractionX Position across the displayed page, 0–1, for the per-mille (‰) value.
 * @param fractionY Position down the displayed page, 0–1.
 */
export function formatPointPoints(
  pointsX: number,
  pointsY: number,
  fractionX: number,
  fractionY: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  correctionFactor: number = 1.0
): FormattedPoint {
  const pdfX = pdfPointsToInches(pointsX, correctionFactor);
  const pdfY = pdfPointsToInches(pointsY, correctionFactor);
  const imgX = pdfPointsToImagePx(pointsX, dpi);
  const imgY = pdfPointsToImagePx(pointsY, dpi);

  return {
    pdfInches: `(${pdfX.toFixed(1)}, ${pdfY.toFixed(1)}) in`,
    imagePx: `(${Math.round(imgX)}, ${Math.round(imgY)}) px`,
    perMille: `(${Math.round(fractionX * 1000)}, ${Math.round(fractionY * 1000)}) ‰`,
  };
}

/** Full measurement result for the area selector. */
export interface FormattedRectangle {
  topLeft: FormattedPoint;
  bottomRight: FormattedPoint;
  sizePdfInches: string;
  sizeImagePx: string;
  sizePerMille: string;
  sizePhysical: string | null;
  sizePhysicalMetric: string | null;
  areaPhysical: string | null; // Square footage or square meters
  areaPhysicalMetric: string | null;
}

/** Formatted enclosed area for any Area-tool shape. */
export interface FormattedSurfaceArea {
  pdf: string;
  image: string;
  physical: string | null;
  physicalMetric: string | null;
}

/**
 * Format an enclosed area already expressed in displayed-page PDF points squared.
 *
 * Length correction and drawing scale both apply in two dimensions, so each factor is squared.
 */
export function formatSurfaceAreaPoints(
  areaPointsSquared: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  scaleFactor: number | null = null,
  correctionFactor: number = 1.0
): FormattedSurfaceArea {
  const correctedSquareInches =
    (Math.abs(areaPointsSquared) / (PDF_POINTS_PER_INCH * PDF_POINTS_PER_INCH)) * correctionFactor * correctionFactor;
  const imageSquarePixels = Math.abs(areaPointsSquared) * Math.pow(dpi / PDF_POINTS_PER_INCH, 2);

  let physical: string | null = null;
  let physicalMetric: string | null = null;
  if (scaleFactor !== null) {
    const realSquareInches = correctedSquareInches * scaleFactor * scaleFactor;
    physical = (realSquareInches / 144).toFixed(1) + ' ft²';
    const squareMeters = realSquareInches / (INCHES_PER_METER * INCHES_PER_METER);
    physicalMetric = squareMeters >= 1 ? squareMeters.toFixed(2) + ' m²' : (squareMeters * 10000).toFixed(1) + ' cm²';
  }

  return {
    pdf: correctedSquareInches.toFixed(2) + ' in²',
    image: Math.round(imageSquarePixels).toLocaleString('en-US') + ' px²',
    physical,
    physicalMetric,
  };
}

/** Format an enclosed screen-pixel area when displayed-page dimensions are unavailable. */
export function formatSurfaceArea(
  screenAreaSquared: number,
  currentScale: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  scaleFactor: number | null = null,
  correctionFactor: number = 1.0
): FormattedSurfaceArea {
  const displayScale = currentScale / HI_DPI_FACTOR;
  const areaPointsSquared = displayScale > 0 ? Math.abs(screenAreaSquared) / (displayScale * displayScale) : 0;
  return formatSurfaceAreaPoints(areaPointsSquared, dpi, scaleFactor, correctionFactor);
}

/** A rectangle: either in PDF points or as fractions (0–1) of the displayed page. */
export interface MeasureRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Format a rectangle defined by two screen-pixel corners.
 * canvasWidth/Height are needed for per-mille (‰) calculation.
 * @param correctionFactor Scanned-PDF correction factor (default 1.0).
 */
export function formatRectangle(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  currentScale: number,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  scaleFactor: number | null = null,
  canvasWidth: number = 0,
  canvasHeight: number = 0,
  correctionFactor: number = 1.0
): FormattedRectangle {
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const width = Math.max(x1, x2) - left;
  const height = Math.max(y1, y2) - top;

  return formatRectanglePoints(
    {
      left: screenToPdfPoints(left, currentScale),
      top: screenToPdfPoints(top, currentScale),
      width: screenToPdfPoints(width, currentScale),
      height: screenToPdfPoints(height, currentScale),
    },
    {
      left: canvasWidth > 0 ? left / canvasWidth : 0,
      top: canvasHeight > 0 ? top / canvasHeight : 0,
      width: canvasWidth > 0 ? width / canvasWidth : 0,
      height: canvasHeight > 0 ? height / canvasHeight : 0,
    },
    dpi,
    scaleFactor,
    correctionFactor
  );
}

/**
 * Format a rectangle given in PDF points (Issue #2442).
 *
 * @param points The rectangle in PDF points, in the displayed orientation of the page.
 * @param fractions The same rectangle as fractions (0–1) of the displayed page, for per-mille (‰).
 * @param scaleFactor Real-world inches per corrected sheet inch, or null when no scale is known.
 * @param correctionFactor Scanned-PDF correction factor (default 1.0).
 */
export function formatRectanglePoints(
  points: MeasureRect,
  fractions: MeasureRect,
  dpi: number = DEFAULT_IMAGE_SEARCH_DPI,
  scaleFactor: number | null = null,
  correctionFactor: number = 1.0
): FormattedRectangle {
  const wPdf = pdfPointsToInches(points.width, correctionFactor);
  const hPdf = pdfPointsToInches(points.height, correctionFactor);
  const wImg = pdfPointsToImagePx(points.width, dpi);
  const hImg = pdfPointsToImagePx(points.height, dpi);

  let sizePhysical: string | null = null;
  let sizePhysicalMetric: string | null = null;
  let areaPhysical: string | null = null;
  let areaPhysicalMetric: string | null = null;
  if (scaleFactor !== null) {
    const wReal = applyScale(wPdf, scaleFactor);
    const hReal = applyScale(hPdf, scaleFactor);
    sizePhysical = `${formatImperial(wReal)} × ${formatImperial(hReal)}`;
    sizePhysicalMetric = `${formatMetric(wReal)} × ${formatMetric(hReal)}`;

    const surface = formatSurfaceAreaPoints(points.width * points.height, dpi, scaleFactor, correctionFactor);
    areaPhysical = surface.physical;
    areaPhysicalMetric = surface.physicalMetric;
  }

  return {
    topLeft: formatPointPoints(points.left, points.top, fractions.left, fractions.top, dpi, correctionFactor),
    bottomRight: formatPointPoints(
      points.left + points.width,
      points.top + points.height,
      fractions.left + fractions.width,
      fractions.top + fractions.height,
      dpi,
      correctionFactor
    ),
    sizePdfInches: `${wPdf.toFixed(1)} × ${hPdf.toFixed(1)} in`,
    sizeImagePx: `${Math.round(wImg)} × ${Math.round(hImg)} px`,
    sizePerMille: `${Math.round(fractions.width * 1000)} × ${Math.round(fractions.height * 1000)} ‰`,
    sizePhysical,
    sizePhysicalMetric,
    areaPhysical,
    areaPhysicalMetric,
  };
}

// ============================================================================
// Scale Parsing
// ============================================================================

/**
 * Parse a drawing scale from free-form text (typically from page_markdown).
 *
 * Supports two formats:
 * 1. Inch-to-feet:  `1/4" = 1'-0"`,  `1" = 10'`,  `3/16" = 1'-0"`, etc.
 *    The drawing-side can be a fraction or whole number of inches.
 *    The real-side is feet (with optional inches after a dash).
 *
 * 2. Ratio:  `1:48`,  `SCALE 1:120`, etc.
 *
 * Returns the multiplier (drawing-inch → real-inches) or null if not found.
 * E.g., `1/4" = 1'-0"` → factor 48  (0.25 drawing inch = 12 real inches → 12/0.25 = 48)
 *       `1" = 10'`      → factor 120 (1 drawing inch = 120 real inches)
 */
export function parseScaleFromText(text: string): { scaleText: string; scaleFactor: number } | null {
  if (!text) return null;

  // ── Pattern 1: Inch-to-feet ────────────────────────────────
  // Captures: optional whole number, optional fraction, inch mark, =, feet, optional inch suffix
  // Examples:   1/4" = 1'-0"    1" = 10'    3/16" = 1'-0"    1 1/2" = 1'-0"
  const inchToFeetRegex =
    /(\d+\s+)?(\d+\/\d+)?[""″]\s*=\s*(\d+(?:\.\d+)?)\s*['′]\s*(?:[-–]\s*(\d+(?:\.\d+)?)\s*[""″]?)?/gi;

  let match: RegExpExecArray | null;
  while ((match = inchToFeetRegex.exec(text)) !== null) {
    const wholeStr = match[1]?.trim(); // e.g., "1" in "1 1/2""
    const fracStr = match[2]; // e.g., "1/4"
    const feetStr = match[3]; // e.g., "10"
    const inchesStr = match[4]; // e.g., "0" in "1'-0""

    // Compute drawing inches
    let drawingInches = 0;
    if (wholeStr) {
      drawingInches += parseFloat(wholeStr);
    }
    if (fracStr) {
      const [num, den] = fracStr.split('/').map(Number);
      if (den > 0) drawingInches += num / den;
    }
    // If neither whole-with-space nor fraction matched, skip
    if (drawingInches === 0) continue;

    // Compute real inches from feet + optional inches
    const realFeet = parseFloat(feetStr) || 0;
    const realInches = parseFloat(inchesStr || '0') || 0;
    const totalRealInches = realFeet * 12 + realInches;

    if (drawingInches > 0 && totalRealInches > 0) {
      const factor = totalRealInches / drawingInches;
      return { scaleText: match[0].trim(), scaleFactor: factor };
    }
  }

  // Handle standalone whole-inch patterns like 1" = 10' that the above might miss
  // because the whole number isn't followed by a space+fraction
  const wholeInchRegex = /(\d+)\s*[""″]\s*=\s*(\d+(?:\.\d+)?)\s*['′]\s*(?:[-–]\s*(\d+(?:\.\d+)?)\s*[""″]?)?/gi;

  while ((match = wholeInchRegex.exec(text)) !== null) {
    const drawingInches = parseFloat(match[1]);
    const realFeet = parseFloat(match[2]) || 0;
    const realInches = parseFloat(match[3] || '0') || 0;
    const totalRealInches = realFeet * 12 + realInches;

    if (drawingInches > 0 && totalRealInches > 0) {
      const factor = totalRealInches / drawingInches;
      return { scaleText: match[0].trim(), scaleFactor: factor };
    }
  }

  // ── Pattern 2: Ratio format (e.g., "1:48", "SCALE 1:120") ─
  const ratioRegex = /(?:scale\s+)?1\s*:\s*(\d+(?:\.\d+)?)/gi;

  while ((match = ratioRegex.exec(text)) !== null) {
    const factor = parseFloat(match[1]);
    if (factor > 1) {
      return { scaleText: match[0].trim(), scaleFactor: factor };
    }
  }

  return null;
}
